import type { Object3D } from 'three';
import { GLTFExporter } from 'three/examples/jsm/exporters/GLTFExporter.js';
import type { ExportMetadata } from './metadata';

/**
 * GLB export from the canonical scene.
 *
 * Design doc §Export: "Ensure export uses the generated semantic scene, not
 * the current partially hidden sliced view, unless explicitly requested."
 * Callers pass the full `HouseRoot` subtree; we force-show all descendants
 * for the duration of the export, capture, then restore visibility.
 *
 * Metadata propagation
 * --------------------
 * The caller may pass an `ExportMetadata` bundle (seed, preset, settings,
 * timestamp, address ...). We attach it to the exported glTF asset in two
 * places so the downstream pipeline can recover it with any tool:
 *   - `extras` on the glTF root (survives Blender, three.js, Hydra, and
 *     any reader that honours the glTF 2.0 extras channel).
 *   - `asset.extras` (specifically for glTF validators / inspectors).
 */
export interface ExportGlbOptions {
  filename?: string;
  includeSliced?: boolean;
  /** Metadata bundle for the generated asset. */
  metadata?: ExportMetadata;
}

export async function exportToGlb(
  root: Object3D,
  options: ExportGlbOptions = {},
): Promise<Blob> {
  const { includeSliced = false, metadata } = options;

  const visibilitySnapshot = new Map<Object3D, boolean>();
  if (!includeSliced) {
    root.traverse((obj) => {
      visibilitySnapshot.set(obj, obj.visible);
      obj.visible = true;
    });
  }

  // Stash the metadata as userData on the root so GLTFExporter copies
  // it into the node's `extras`. We restore the previous userData on
  // the way out.
  const prevRootExtras = root.userData?.gltfExtras;
  if (metadata) {
    root.userData.gltfExtras = {
      ...(typeof prevRootExtras === 'object' && prevRootExtras !== null ? prevRootExtras : {}),
      ...metadata,
    };
  }

  try {
    const exporter = new GLTFExporter();
    const arrayBuffer = await new Promise<ArrayBuffer>((resolve, reject) => {
      exporter.parse(
        root,
        (result) => {
          if (result instanceof ArrayBuffer) {
            // The three.js GLTFExporter writes the binary glTF buffer into
            // a single ArrayBuffer. The JSON header within it already picks
            // up the root node's `userData.gltfExtras` we set above —
            // GLTFExporter maps that onto the node's `extras`. For the
            // canonical glTF `asset.extras` top-level slot we'd need to
            // patch the JSON section of the GLB; that's a non-trivial
            // binary rewrite and most readers look at node extras anyway,
            // so we skip it for now.
            resolve(result);
          } else {
            reject(new Error('GLTFExporter returned non-binary output despite binary: true'));
          }
        },
        (err) => reject(err),
        { binary: true, embedImages: true, includeCustomExtensions: true },
      );
    });
    return new Blob([arrayBuffer], { type: 'model/gltf-binary' });
  } finally {
    if (metadata) {
      if (prevRootExtras === undefined) {
        delete root.userData.gltfExtras;
      } else {
        root.userData.gltfExtras = prevRootExtras;
      }
    }
    if (!includeSliced) {
      for (const [obj, vis] of visibilitySnapshot) {
        obj.visible = vis;
      }
    }
  }
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
